import {
  CircleAlert,
  CircleCheck,
  Clock,
  Eye,
  Loader,
  Newspaper,
  Send,
  type LucideIcon,
} from "lucide-react"

import { Badge, cn } from "@atlant/ui"

// Pill tints. Statuses that should look alike share one, so they can't drift apart.
const tones = {
  muted: "bg-muted text-muted-foreground",
  emerald:
    "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
  indigo:
    "bg-indigo-50 text-indigo-600 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30",
  orange:
    "bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-500/30",
  fuchsia:
    "bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200 dark:bg-fuchsia-500/15 dark:text-fuchsia-300 dark:border-fuchsia-500/30",
  amber:
    "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
  primary:
    "bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:border-primary/40",
}

type StatusConfig = {
  label: string
  icon: LucideIcon | null
  tone: string
}

const statusConfig = {
  // Month workflow statuses, tinted pills as in the year view design
  closed: { label: "Closed", icon: CircleCheck, tone: tones.emerald },
  send: { label: "Send", icon: Send, tone: tones.indigo },
  inReview: { label: "In review", icon: Eye, tone: tones.orange },
  new: { label: "New", icon: Newspaper, tone: tones.muted },
  notStarted: { label: "Not started", icon: null, tone: tones.muted },
  // The month inbox's Status pill, plain text as in the design
  late: { label: "Late", icon: null, tone: tones.fuchsia },
  needsReview: { label: "Needs review", icon: CircleAlert, tone: tones.amber },
  // Upload queue statuses, tinted pills like the late tag
  classifying: { label: "Classifying", icon: Loader, tone: tones.muted },
  awaitingReview: { label: "Awaiting review", icon: Clock, tone: tones.amber },
  approved: { label: "Approved", icon: CircleCheck, tone: tones.primary },
} satisfies Record<string, StatusConfig>

export type InvoiceStatus = keyof typeof statusConfig

interface StatusBadgeProps {
  status: InvoiceStatus
  className?: string
}

export const StatusBadge = ({ status, className }: StatusBadgeProps) => {
  const { label, icon: Icon, tone }: StatusConfig = statusConfig[status]

  return (
    <Badge variant="outline" className={cn("gap-1 rounded-full", tone, className)}>
      {Icon && <Icon className="size-3" />}
      {label}
    </Badge>
  )
}
